/**
 * UpgradePreviewModal — iter-230bw Phase 4
 * ----------------------------------------
 * Shows the pro-rated diff that the vendor will be charged for an
 * immediate plan upgrade. Triggers POST /api/subscription-billing/upgrade,
 * which returns a one-time Razorpay Order. We open Razorpay Checkout
 * inline using the returned order_id.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { ArrowUpCircle, Loader2 } from 'lucide-react';

const formatINR = (v) =>
  v == null ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (typeof window !== 'undefined' && window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function UpgradePreviewModal({
  isOpen, onClose, fromPlanCode, toPlanCode, toPlanName, onSuccess,
}) {
  const { apiCall, user } = useAuth();
  const [preview, setPreview] = useState(null);   // { amount_rupees, remaining_ratio, order_id, razorpay_key_id }
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);

  // Single-call flow — POST /upgrade returns the order + diff amount.
  useEffect(() => {
    if (!isOpen) { setPreview(null); return; }
    setLoading(true);
    apiCall('/api/subscription-billing/upgrade', {
      method: 'POST',
      body: JSON.stringify({ new_plan_code: toPlanCode }),
    })
      .then((res) => setPreview(res))
      .catch((e) => {
        toast.error(e?.message || 'Could not preview upgrade.');
        onClose?.();
      })
      .finally(() => setLoading(false));
  }, [isOpen, toPlanCode]); // eslint-disable-line

  const handlePay = async () => {
    if (!preview?.order_id) return;
    const ok = await loadRazorpayScript();
    if (!ok) {
      toast.error('Could not load Razorpay. Check your connection and retry.');
      return;
    }
    setPaying(true);
    const rzp = new window.Razorpay({
      key: preview.razorpay_key_id,
      amount: preview.amount,
      currency: preview.currency || 'INR',
      order_id: preview.order_id,
      name: 'Aniyor Subscription Upgrade',
      description: `Upgrade ${fromPlanCode} → ${toPlanCode}`,
      prefill: {
        name: user?.name,
        email: user?.email,
        contact: user?.mobile || user?.phone,
      },
      theme: { color: '#1a1a1a' },
      handler: (resp) => {
        toast.success('Payment successful! Your plan has been upgraded.');
        onSuccess?.(resp);
        onClose?.();
      },
      modal: {
        ondismiss: () => setPaying(false),
      },
    });
    rzp.open();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="upgrade-preview-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowUpCircle className="h-5 w-5 text-primary" />
            Upgrade to {toPlanName || toPlanCode}
          </DialogTitle>
          <DialogDescription>
            Your new plan takes effect immediately. We&apos;ll charge a pro-rated
            difference for the rest of this billing period — the full new
            price kicks in from your next renewal.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-8 flex items-center justify-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Calculating diff…
          </div>
        )}

        {preview && (
          <div className="space-y-3 py-2" data-testid="upgrade-diff-summary">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">From plan</span>
              <span className="font-medium capitalize">{fromPlanCode}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">To plan</span>
              <span className="font-medium capitalize">{preview.to_plan_code || toPlanCode}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Remaining cycle</span>
              <span className="font-medium">{Math.round((preview.remaining_ratio || 0) * 100)}%</span>
            </div>
            <div className="border-t pt-3 mt-3 flex justify-between items-baseline">
              <span className="text-sm font-semibold">Pro-rated diff today</span>
              <span className="text-2xl font-bold text-primary" data-testid="upgrade-diff-amount">
                {formatINR(preview.amount_rupees)}
              </span>
            </div>
            <p className="text-xs text-slate-500">
              + GST as applicable on next invoice. Your storefront limits update the moment payment succeeds.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={paying}>Cancel</Button>
          <Button
            onClick={handlePay}
            disabled={loading || paying || !preview?.order_id}
            data-testid="upgrade-pay-button"
          >
            {paying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Pay {formatINR(preview?.amount_rupees)} & upgrade
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
